import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AppFile,
  BuyerContact,
  CollectionActivityReference,
  CollectionActivityWithUser,
  extractPlainTextFromLexical as extractPlainText,
} from '@dill/dill-shared';
import { Download, Trash } from 'phosphor-react';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/store';
import {
  updateCollectionActivity,
  getBuyerCollectionActivities,
} from '../../../../../../redux/services/collectionActivityService';
import { DillModal } from '../../../../../general/Dill/DillModal';
import { DillButton } from '../../../../../general/Dill/DillButton';
import { openMessageModal } from '../../../../../../redux/globalSlices/genericSlice';
import LexicalEditor from '../../../../../general/LexicalEditor/LexicalEditor';
import ActivityReferenceSelector from '../../ActivityReferenceSelector/ActivityReferenceSelector';
import AppFilePicker from '../../../../../general/AppFilePicker/AppFilePicker';
import { downloadFile } from '../../../../../../utils/helpers';

interface EditNoteModalProps {
  open: boolean;
  activity: CollectionActivityWithUser | null;
  onClose: () => void;
  onSuccess?: () => void;
  contacts?: BuyerContact[];
}

function EditNoteModal({ open, activity, onClose, onSuccess, contacts }: EditNoteModalProps) {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { loadingList } = useAppSelector((state) => state.collectionActivities);

  const [noteContent, setNoteContent] = useState('');
  const [references, setReferences] = useState<CollectionActivityReference[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [noteError, setNoteError] = useState('');
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [currentAttachments, setCurrentAttachments] = useState<AppFile[]>([]);

  const isLoading = loadingList.includes('updateCollectionActivity');

  // Derive initial content from activity - memoized to prevent unnecessary re-renders
  const initialContent = useMemo(() => {
    if (!activity?.noteDetails?.content) return undefined;
    const content = activity.noteDetails.content;
    return typeof content === 'string' ? content : JSON.stringify(content);
  }, [activity?.noteDetails?.content]);

  // Initialize references and attachments from activity
  useEffect(() => {
    setReferences(activity?.references || []);
    setCurrentAttachments(activity?.noteDetails?.attachments || []);
  }, [activity]);

  const handleEditorChange = useCallback(
    (content: string) => {
      setNoteContent(content);
      if (noteError) setNoteError('');
    },
    [noteError]
  );

  const handleSubmit = async () => {
    const contentToSubmit = noteContent || initialContent;

    if (!contentToSubmit || !extractPlainText(contentToSubmit)) {
      setNoteError('Please enter note content');
      return;
    }

    if (!currentSupplier?.id || !activity) {
      return;
    }

    if (!user?.id) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Build edit history entry
      const previousContent = activity.noteDetails?.content;
      const editHistoryEntry = {
        previousContent: previousContent,
        versionCreatedAt: activity.createdAt || new Date(),
        replacedAt: new Date(),
        replacedBy: user.id,
      };

      const existingEditHistory = activity.noteDetails?.editHistory || [];

      const result = await dispatch(
        updateCollectionActivity({
          supplierId: currentSupplier.id,
          activityId: activity.id,
          updateData: {
            noteDetails: {
              content: JSON.parse(contentToSubmit),
              attachments: currentAttachments,
              editHistory: [...existingEditHistory, editHistoryEntry],
            },
            references,
          },
          noteAttachmentsFiles: attachmentFiles,
          currentNoteAttachments: currentAttachments,
        })
      );

      if (result.payload && (result.payload as any).success) {
        // Refresh the activities list
        dispatch(
          getBuyerCollectionActivities({
            supplierId: currentSupplier.id,
            buyerId: activity.buyerId,
          })
        );
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Failed to update note:', error);
      dispatch(
        openMessageModal({
          title: 'Error',
          subTitle: 'Failed to update note. Please try again.',
          buttonText: 'OK',
          modalType: 'ERROR',
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title="Edit Note"
      maxWidth="576px"
      footer={
        <>
          <DillButton
            onClick={handleClose}
            variant="secondary"
            isDisabled={isLoading}
            label="Cancel"
          />
          <DillButton
            onClick={handleSubmit}
            variant="primary"
            isLoading={isLoading}
            label="Save Changes"
          />
        </>
      }>
      <LexicalEditor
        label="Note Content"
        isRequired
        initialContent={initialContent}
        onChange={handleEditorChange}
        placeholder="Enter your note..."
        minHeight="120px"
        editable={!isLoading}
        errorText={noteError}
      />

      {activity?.noteDetails?.editHistory && activity.noteDetails.editHistory.length > 0 && (
        <div className="text-xs text-gray-500">
          This note has been edited {activity.noteDetails.editHistory.length} time(s)
        </div>
      )}

      <ActivityReferenceSelector
        label="References (Optional)"
        buyerId={activity?.buyerId || ''}
        value={references}
        onChange={setReferences}
        isDisabled={isLoading}
        contacts={contacts}
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Attachments (Optional)
        </label>
        {currentAttachments && currentAttachments.length > 0 && (
          <div className="mb-2 border rounded">
            {currentAttachments.map((attachment, i) => (
              <div
                key={i}
                className="flex items-center justify-between border-b last:border-b-0 px-2 py-1">
                <p className="text-xs truncate max-w-[300px]">{attachment.name}</p>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className="p-1 hover:bg-gray-100 rounded"
                    onClick={(e) => {
                      e.preventDefault();
                      downloadFile(attachment.url || '', attachment.name || 'attachment');
                    }}>
                    <Download size={14} className="text-gray-500" />
                  </button>
                  <button
                    type="button"
                    className="p-1 hover:bg-gray-100 rounded"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentAttachments(currentAttachments.filter((_, ii) => ii !== i));
                    }}>
                    <Trash size={14} className="text-red-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <AppFilePicker
          onSelectFile={(files: File[]) => {
            setAttachmentFiles(files);
          }}
        />
      </div>
    </DillModal>
  );
}

export default EditNoteModal;
